/**
 * CreativeMedia — the ONE way to render a creative that may be an image OR a video.
 *
 * Every surface that shows a creative (composer tile, thread bubble, report carousel,
 * reading view, report panel) branches on the same predicate and renders the same
 * <video>/<img> pair, so a video creative can never regress to a broken <img> on a
 * surface someone forgot to touch. The video renders muted/inline/metadata-only (a
 * preview, not a player); a codec the browser can't decode degrades to a labelled
 * placeholder instead of a black box — the analysis runs server-side either way.
 *
 * A metadata-only <video> with no poster paints frame 0, and frame 0 of a fade-in-from-
 * black ad is black, so the tile would read as an empty rectangle. Once metadata lands
 * the preview seeks a hair past frame 0 (10% in, capped at 0.5s) so a real frame paints.
 * The seek is applied to the ELEMENT, never to `src` — a media fragment would change the
 * src every call site asserts on. Because the seek forces a media-data range request that
 * `preload="metadata"` never issued, a host that ignores Range can now error where it
 * previously did not; that first error rewinds to frame 0 and retries once rather than
 * degrading a black-but-present tile into the placeholder.
 *
 * All of that bookkeeping is per-creative, so it is re-armed the moment `src` changes.
 * React reuses an instance when the element keeps its position, and the composer keys
 * chips by name+index, so removing the first of two identically-named files hands a tile
 * a DIFFERENT creative on the same instance. Carrying a spent retry across that hand-off
 * would silently disable the seek for the new video, and carrying a spent `seekAttempted`
 * would let the one-shot retry swallow a GENUINE decode error on the new src: an element
 * that already errored fires no second `error` without a new fetch, so the tile would go
 * black and silent instead of showing the labelled fallback. The reset runs during render
 * rather than in an effect, because an effect lands after paint, where the new element's
 * own `loadedmetadata` / `error` can already have fired against the old creative's flags.
 *
 * Two opt-in extras ride on the same metadata event, both inert unless a call site passes
 * them. `onMeta` reports the creative's length and intrinsic size (a duration chip has no
 * other source for them). `playOnHover` previews the clip on pointer enter and on keyboard
 * focus, and on leave/blur rewinds to the position the seek chose rather than to frame 0,
 * so the tile lands back on the real frame instead of the black one this file exists to
 * avoid. A user who asked for reduced motion gets the still tile, never the autoplay.
 */
import { SyntheticEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isYoutubeUrl, youtubeThumbnailUrl, youtubeVideoId } from '@digital-twins/shared-types'

/** Is this creative a video? Case-insensitive on mimeType, with a data:video/ sniff for
 *  creatives that carry no mimeType (e.g. a persisted src-only report block). YouTube
 *  watch URLs are video even when mimeType is missing. The single shared predicate —
 *  chat/report surfaces must import THIS, not hand-roll startsWith. */
export function isVideoCreativeLike(c: { mimeType?: string | null; src?: string | null }): boolean {
  if (c.mimeType) return c.mimeType.toLowerCase().startsWith('video/')
  if (isYoutubeUrl(c.src ?? '')) return true
  return /^data:video\//i.test(c.src ?? '')
}

interface CreativeMediaProps {
  /** data: or http(s) URL for the creative. */
  src: string
  /** MIME (e.g. 'video/mp4'); inferred from a data: src when absent. */
  mimeType?: string
  /** Alt text for images / aria-label for videos. */
  alt?: string
  /** Applied to whichever element renders (img, video, or the error placeholder), so
   *  each call site keeps its own layout untouched. */
  className?: string
  dataTestId?: string
  /** Fired when video metadata lands, with the creative's intrinsic size and length.
   *  Images never fire it. Call sites use it for a duration chip. */
  onMeta?: (meta: { durationSec: number; width: number; height: number }) => void
  /** Play muted on pointer enter and on keyboard focus; pause and rewind to the
   *  seeked poster frame on leave/blur. Videos only, ignored for images. */
  playOnHover?: boolean
}

/** Where the poster frame sits: a hair past frame 0 (10% in, capped at 0.5s). The metadata
 *  seek and the rewind that ends a hover preview read the SAME target, so a tile that
 *  played always returns to the frame it was showing. */
function posterSeekTarget(duration: number): number {
  return Math.min(0.5, duration * 0.1)
}

/** Does the viewer want motion held back? `matchMedia` is missing in some jsdom configs and
 *  in SSR, and an absent setting reads as "no preference" rather than as a throw. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  const query = window.matchMedia('(prefers-reduced-motion: reduce)')
  return Boolean(query && query.matches)
}

/** A video src that carries NO bytes (`data:video/…;base64,` with nothing after the comma).
 *  This is what a persisted video whose hosting FAILED rehydrates to: the write seam strips
 *  unhosted video base64 (it would kill localStorage + the session mirror), leaving a
 *  byte-free marker. Renderable only as the "re-add" placeholder, never a <video>. */
function isByteFreeVideoSrc(src: string): boolean {
  return /^data:video\/[^;,]*;base64,?$/i.test(src.trim())
}

export function CreativeMedia({
  src,
  mimeType,
  alt,
  className,
  dataTestId,
  onMeta,
  playOnHover,
}: CreativeMediaProps) {
  const { t } = useTranslation('app')
  // A browser that can't decode the container (e.g. Safari-only HEVC in Chrome) fires
  // onError; swap in a labelled placeholder so the tile explains itself.
  const [videoFailed, setVideoFailed] = useState(false)

  // Refs, not state: the seek bookkeeping must never re-render the tile (a re-render
  // would reload the element and restart the very seek we are tracking).
  const seekAttemptedRef = useRef(false)
  const seekRetriedRef = useRef(false)

  // Where this element actually sits when it is not playing: 0 until the seek lands, then
  // the seeked poster position. A hover preview rewinds to THIS, so the tile returns to the
  // frame it was showing rather than to the black frame 0.
  const posterTimeRef = useRef(0)

  // The creative this instance is currently rendering. Held in state, not a ref, so the
  // `videoFailed` reset below is a render-phase update React actually honours (it re-runs
  // this component before committing, so nothing paints from the previous creative's
  // verdict). Only a genuine src change enters the branch: a re-render with the same src
  // leaves an in-flight seek, a spent retry and a latched failure exactly as they were.
  const [renderedSrc, setRenderedSrc] = useState(src)
  if (renderedSrc !== src) {
    setRenderedSrc(src)
    seekAttemptedRef.current = false
    seekRetriedRef.current = false
    // The new creative has not been seeked yet, so its resting frame is 0 again.
    posterTimeRef.current = 0
    // A new creative deserves a fresh verdict: the old one's undecodable container says
    // nothing about this one, and without the reset a tile that failed once stays a
    // placeholder forever, however good the src it is later handed.
    setVideoFailed(false)
  }

  const onLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = event.currentTarget
    // The retry is spent — the seek already cost us one error, so leave frame 0 alone
    // rather than looping error → rewind → re-announce → seek.
    if (seekRetriedRef.current) {
      return
    }
    // `duration` is typed `number` but real implementations hand back NaN (metadata not
    // truly parsed), Infinity (live stream) and even null. Number.isFinite rejects all
    // three; Number.isNaN would wave null and Infinity straight through.
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      return
    }
    seekAttemptedRef.current = true
    posterTimeRef.current = posterSeekTarget(video.duration)
    video.currentTime = posterTimeRef.current
    // Reported after the seek, so a call site whose handler throws can never cost this tile
    // the poster frame. `videoWidth` / `videoHeight` read 0 until metadata lands, and the
    // duration is the one the guard above just vouched for.
    onMeta?.({ durationSec: video.duration, width: video.videoWidth, height: video.videoHeight })
  }

  const onVideoError = (event: SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = event.currentTarget
    // An error that follows OUR seek is most likely the range request the host refused,
    // not an undecodable creative. Rewind and retry once; flags flip before the rewind so
    // a synchronous second error cannot claim the retry twice.
    if (seekAttemptedRef.current && !seekRetriedRef.current) {
      seekAttemptedRef.current = false
      seekRetriedRef.current = true
      video.currentTime = 0
      // Frame 0 is where this element now rests, so that is where a hover preview rewinds.
      posterTimeRef.current = 0
      return
    }
    setVideoFailed(true)
  }

  // Hover/focus preview. Pointer and keyboard drive the same pair, so the feature is never
  // mouse-only, and both are wired up only when the call site opted in.
  const startPreview = (event: SyntheticEvent<HTMLVideoElement, Event>) => {
    if (prefersReducedMotion()) {
      return
    }
    const video = event.currentTarget
    try {
      const started = video.play?.()
      // play() rejects on an interrupted play and under an autoplay policy, and jsdom has no
      // media stack at all. Swallow it: a preview that does not start is a still tile, not
      // an unhandled rejection in the console or in a test run.
      if (started && typeof started.catch === 'function') {
        started.catch(() => {})
      }
    } catch {
      // Same reasoning for a synchronous throw (jsdom's not-implemented media stack).
    }
  }

  const stopPreview = (event: SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = event.currentTarget
    try {
      video.pause?.()
    } catch {
      // Pausing an element jsdom never started is not a real failure.
    }
    video.currentTime = posterTimeRef.current
  }

  if (!isVideoCreativeLike({ mimeType, src })) {
    return <img src={src} alt={alt ?? t('chatChrome.creativeAlt')} data-testid={dataTestId} className={className} />
  }

  const ytId = youtubeVideoId(src)
  if (ytId) {
    return (
      <img
        src={youtubeThumbnailUrl(ytId)}
        alt={alt ?? t('chatChrome.youtubeVideoAlt')}
        data-testid={dataTestId}
        className={className}
      />
    )
  }

  if (isByteFreeVideoSrc(src)) {
    return (
      <div
        role="img"
        aria-label={alt ?? t('chatChrome.videoCreativeAria')}
        data-testid={dataTestId ? `${dataTestId}-video-missing` : 'creative-media-video-missing'}
        className={className}
      >
        <span className="block p-2 text-center text-[11px] leading-snug text-muted-foreground">
          {t('chatChrome.videoUploadIncomplete')}
        </span>
      </div>
    )
  }

  if (videoFailed) {
    return (
      <div
        role="img"
        aria-label={alt ?? t('chatChrome.videoCreativeAria')}
        data-testid={dataTestId ? `${dataTestId}-video-fallback` : 'creative-media-video-fallback'}
        className={className}
      >
        <span className="block p-2 text-center text-[11px] leading-snug text-muted-foreground">
          {t('chatChrome.videoPreviewUnavailable')}
        </span>
      </div>
    )
  }

  return (
    <video
      src={src}
      muted
      playsInline
      preload="metadata"
      onLoadedMetadata={onLoadedMetadata}
      onError={onVideoError}
      // Left undefined unless the call site opted in, so the 15 existing ones render the
      // exact element they render today. `tabIndex` comes with the feature: without it the
      // element never takes focus and the keyboard half of the preview would be dead.
      onMouseEnter={playOnHover ? startPreview : undefined}
      onMouseLeave={playOnHover ? stopPreview : undefined}
      onFocus={playOnHover ? startPreview : undefined}
      onBlur={playOnHover ? stopPreview : undefined}
      tabIndex={playOnHover ? 0 : undefined}
      aria-label={alt ?? t('chatChrome.videoCreativeAria')}
      data-testid={dataTestId}
      className={className}
    />
  )
}

export default CreativeMedia
